import { createFileRoute } from '@tanstack/react-router';
import {
  Alert, Badge, Button, Card, Group, NumberInput, Select, Stack, Table, Tabs, Text, TextInput,
  ActionIcon, Modal, Loader,
} from '@mantine/core';
import { PageHeader } from '@/shared/components/PageHeader';
import { TimeInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { IconBuilding, IconCheck, IconClock, IconEdit, IconInfoCircle, IconPlus, IconTrash, IconUserCircle } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  DAY_LABELS, WEEKDAYS, fetchShifts, fetchSites, fetchEmployees, fetchSiteWorkingTimes,
  fetchEmployeeWorkingHours, upsertSiteWorkingTime, deleteSiteWorkingTime,
  upsertEmployeeWorkingHour, deleteEmployeeWorkingHour,
  type SiteWorkingTimeRow, type EmployeeWorkingHourRow, type ShiftRow,
} from '@/features/working-hours/api';
import { notifySuccess, notifyError } from '@/shared/lib/notify';
import { useCrudResource } from '@/shared/hooks/useCrudResource';

export const Route = createFileRoute('/_app/admin/working-hours')({ component: WorkingHoursPage });

function WorkingHoursPage() {
  return (
    <Stack>
      <PageHeader
        title="Orari di lavoro"
        description="Turni standard, default per sede e override per singoli dipendenti."
      />
      <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
        Configura i turni standard, applicali alle sedi come default e definisci override per singoli dipendenti quando serve.
      </Alert>
      <Tabs defaultValue="sites" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="sites" leftSection={<IconBuilding size={16} />}>Sedi</Tabs.Tab>
          <Tabs.Tab value="employees" leftSection={<IconUserCircle size={16} />}>Dipendenti</Tabs.Tab>
          <Tabs.Tab value="shifts" leftSection={<IconClock size={16} />}>Turni base</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="sites" pt="md"><SitesPanel /></Tabs.Panel>
        <Tabs.Panel value="employees" pt="md"><EmployeesPanel /></Tabs.Panel>
        <Tabs.Panel value="shifts" pt="md"><ShiftsPanel /></Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

// ============ TURNI ============
function ShiftsPanel() {
  const crud = useCrudResource<ShiftRow>({
    resource: 'erp-shifts',
    queryKey: ['wh-shifts'],
    list: { sort: ['name'], page: { size: 100 } },
    labels: { create: 'Turno creato', update: 'Turno aggiornato', remove: 'Turno eliminato' },
    onCreateSuccess: () => close(),
    onUpdateSuccess: () => close(),
  });
  const [editing, setEditing] = useState<ShiftRow | null>(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [form, setForm] = useState<{ name: string; description: string; startTime: string; standardWorkingTime: number }>({
    name: '', description: '', startTime: '09:00', standardWorkingTime: 8,
  });

  function startEdit(s: ShiftRow | null) {
    setEditing(s);
    setForm({
      name: s?.name ?? '',
      description: s?.description ?? '',
      startTime: (s?.startTime ?? '09:00:00').slice(0, 5),
      standardWorkingTime: s?.standardWorkingTime ?? 8,
    });
    open();
  }

  const handleSave = () => {
    const attrs = {
      name: form.name,
      description: form.description,
      startTime: form.startTime.length === 5 ? `${form.startTime}:00` : form.startTime,
      standardWorkingTime: form.standardWorkingTime,
    } as Record<string, unknown>;
    if (editing) crud.update.mutate({ id: editing.id, attributes: attrs });
    else crud.create.mutate({ attributes: attrs });
  };
  const saving = crud.create.isPending || crud.update.isPending;

  return (
    <Stack>
      <Group justify="space-between">
        <Text c="dimmed">I turni definiscono l'orario base (ora di inizio e ore standard giornaliere).</Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => startEdit(null)}>Nuovo turno</Button>
      </Group>
      <Card withBorder padding={0} radius="md">
        {crud.isLoading ? <Loader m="md" /> : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Descrizione</Table.Th>
                <Table.Th>Inizio</Table.Th>
                <Table.Th>Ore standard</Table.Th>
                <Table.Th style={{ width: 100 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {crud.items.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td><Text fw={600}>{s.name}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{s.description}</Text></Table.Td>
                  <Table.Td>{(s.startTime ?? '').slice(0, 5)}</Table.Td>
                  <Table.Td><Badge variant="light">{s.standardWorkingTime}h</Badge></Table.Td>
                  <Table.Td>
                    <Group gap={4}>
                      <ActionIcon variant="subtle" onClick={() => startEdit(s)} aria-label="Modifica turno"><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => crud.remove.mutate(s.id)} aria-label="Elimina turno"><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {crud.items.length === 0 && !crud.isLoading && (
                <Table.Tr><Table.Td colSpan={5}><Text ta="center" c="dimmed" py="md">Nessun turno definito.</Text></Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal opened={opened} onClose={close} title={editing ? 'Modifica turno' : 'Nuovo turno'}>
        <Stack>
          <TextInput label="Nome" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.currentTarget.value }))} placeholder="es. Standard 9-18" />
          <TextInput label="Descrizione" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.currentTarget.value }))} />
          <TimeInput label="Inizio turno" value={form.startTime} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((f) => ({ ...f, startTime: e.currentTarget.value }))} />
          <NumberInput label="Ore lavorative standard" min={1} max={24} value={form.standardWorkingTime} onChange={(v) => setForm((f) => ({ ...f, standardWorkingTime: Number(v) || 0 }))} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={close}>Annulla</Button>
            <Button leftSection={<IconCheck size={16} />} loading={saving} onClick={handleSave}>Salva</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

// ============ SEDI: orario settimanale default ============
function SitesPanel() {
  const sites = useQuery({ queryKey: ['wh-sites'], queryFn: () => fetchSites() });
  const shifts = useQuery({ queryKey: ['wh-shifts'], queryFn: () => fetchShifts() });
  const [siteId, setSiteId] = useState<string | null>(null);

  const sitesList = sites.data?.data ?? [];
  const selectedSiteId = siteId ?? sitesList[0]?.id ?? null;

  return (
    <Stack>
      <Group>
        <Select
          label="Sede"
          data={sitesList.map((s) => ({ value: s.id, label: s.name ?? s.id }))}
          value={selectedSiteId}
          onChange={setSiteId}
          searchable
          w={320}
        />
      </Group>
      {selectedSiteId && (
        <WeeklyEditor
          key={selectedSiteId}
          mode="site"
          ownerId={selectedSiteId}
          shifts={shifts.data?.data ?? []}
        />
      )}
    </Stack>
  );
}

// ============ DIPENDENTI: override settimanale ============
function EmployeesPanel() {
  const employees = useQuery({ queryKey: ['wh-employees'], queryFn: () => fetchEmployees() });
  const [empId, setEmpId] = useState<string | null>(null);

  const list = employees.data?.data ?? [];
  const selected = empId ?? list[0]?.id ?? null;

  return (
    <Stack>
      <Group>
        <Select
          label="Dipendente"
          data={list.map((e) => ({ value: e.id, label: `${e.lastName ?? ''} ${e.firstName ?? ''}`.trim() || e.id }))}
          value={selected}
          onChange={setEmpId}
          searchable
          w={360}
        />
      </Group>
      {selected && (
        <WeeklyEditor
          key={selected}
          mode="employee"
          ownerId={selected}
        />
      )}
    </Stack>
  );
}

// ============ Editor settimanale shared ============
interface WeeklyEditorProps {
  mode: 'site' | 'employee';
  ownerId: string;
  shifts?: ShiftRow[];
}

function WeeklyEditor({ mode, ownerId, shifts }: WeeklyEditorProps) {
  const qc = useQueryClient();
  const isSite = mode === 'site';
  const queryKey = isSite ? ['wh-site-wt', ownerId] : ['wh-emp-wh', ownerId];

  const rows = useQuery<{ data: (SiteWorkingTimeRow | EmployeeWorkingHourRow)[] }>({
    queryKey,
    queryFn: async () => {
      const r = isSite ? await fetchSiteWorkingTimes(ownerId) : await fetchEmployeeWorkingHours(ownerId);
      return { data: r.data as (SiteWorkingTimeRow | EmployeeWorkingHourRow)[] };
    },
  });

  const byDay = useMemo(() => {
    const m: Record<number, SiteWorkingTimeRow | EmployeeWorkingHourRow> = {};
    for (const r of rows.data?.data ?? []) {
      if (r.day != null) m[r.day] = r;
    }
    return m;
  }, [rows.data]);

  const save = useMutation({
    mutationFn: async ({ day, patch, existing, shiftIdOverride }: { day: number; patch: Record<string, unknown>; existing?: SiteWorkingTimeRow | EmployeeWorkingHourRow; shiftIdOverride?: string }) => {
      if (isSite) {
        const shiftId = shiftIdOverride ?? (existing as SiteWorkingTimeRow | undefined)?.shift?.id ?? shifts?.[0]?.id;
        if (!shiftId) throw new Error('Nessun turno disponibile: crea prima un turno base.');
        return upsertSiteWorkingTime(existing as SiteWorkingTimeRow | undefined, ownerId, shiftId, { day, ...patch });
      }
      return upsertEmployeeWorkingHour(existing as EmployeeWorkingHourRow | undefined, ownerId, { day, ...patch });
    },
    onSuccess: () => { notifySuccess('Salvato'); qc.invalidateQueries({ queryKey }); },
    onError: (err) => notifyError(err),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => isSite ? deleteSiteWorkingTime(id) : deleteEmployeeWorkingHour(id),
    onSuccess: () => { notifySuccess('Rimosso'); qc.invalidateQueries({ queryKey }); },
    onError: (err) => notifyError(err),
  });

  if (rows.isLoading) return <Loader />;

  return (
    <Card withBorder padding={0} radius="md">
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 130 }}>Giorno</Table.Th>
            {!isSite && <Table.Th style={{ width: 110 }}>Inizio</Table.Th>}
            {!isSite && <Table.Th style={{ width: 110 }}>Fine</Table.Th>}
            {isSite && <Table.Th style={{ width: 200 }}>Turno</Table.Th>}
            <Table.Th style={{ width: 110 }}>Flex inizio (min)</Table.Th>
            <Table.Th style={{ width: 110 }}>Flex fine (min)</Table.Th>
            <Table.Th style={{ width: 110 }}>Pausa min (min)</Table.Th>
            <Table.Th style={{ width: 110 }}>Pausa max (min)</Table.Th>
            {!isSite && <Table.Th style={{ width: 100 }}>Ore giornaliere</Table.Th>}
            <Table.Th style={{ width: 60 }}></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {WEEKDAYS.map((day) => {
            const existing = byDay[day];
            const isEmp = !isSite;
            const r = existing as EmployeeWorkingHourRow | undefined;
            return (
              <Table.Tr key={day}>
                <Table.Td><Text fw={600}>{DAY_LABELS[day]}</Text></Table.Td>
                {isEmp && (
                  <>
                    <Table.Td>
                      <TimeInput
                        size="xs"
                        value={(r?.startTime ?? '').slice(0, 5)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => save.mutate({ day, patch: { startTime: e.currentTarget.value ? `${e.currentTarget.value}:00` : null }, existing })}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TimeInput
                        size="xs"
                        value={(r?.endTime ?? '').slice(0, 5)}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => save.mutate({ day, patch: { endTime: e.currentTarget.value ? `${e.currentTarget.value}:00` : null }, existing })}
                      />
                    </Table.Td>
                  </>
                )}
                {isSite && (
                  <Table.Td>
                    <Select
                      size="xs"
                      data={(shifts ?? []).map((s) => ({ value: s.id, label: s.name ?? s.id }))}
                      value={(existing as SiteWorkingTimeRow | undefined)?.shift?.id ?? shifts?.[0]?.id ?? null}
                      onChange={(v) => v && save.mutate({ day, patch: {}, existing, shiftIdOverride: v })}
                      allowDeselect={false}
                    />
                  </Table.Td>
                )}
                <Table.Td>
                  <NumberInput size="xs" min={0} max={120} value={(existing as { startFlexibility?: number | null } | undefined)?.startFlexibility ?? 0}
                    onBlur={(e) => save.mutate({ day, patch: { startFlexibility: Number(e.currentTarget.value) || 0 }, existing })}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0} max={120} value={(existing as { endFlexibility?: number | null } | undefined)?.endFlexibility ?? 0}
                    onBlur={(e) => save.mutate({ day, patch: { endFlexibility: Number(e.currentTarget.value) || 0 }, existing })}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0} max={240} value={(existing as { minimumBreakDuration?: number | null } | undefined)?.minimumBreakDuration ?? 0}
                    onBlur={(e) => save.mutate({ day, patch: { minimumBreakDuration: Number(e.currentTarget.value) || 0 }, existing })}
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput size="xs" min={0} max={240} value={(existing as { maximumBreakDuration?: number | null } | undefined)?.maximumBreakDuration ?? 0}
                    onBlur={(e) => save.mutate({ day, patch: { maximumBreakDuration: Number(e.currentTarget.value) || 0 }, existing })}
                  />
                </Table.Td>
                {isEmp && (
                  <Table.Td>
                    <NumberInput size="xs" min={0} max={24} value={r?.dailyWorkingTime ?? 0}
                      onBlur={(e) => save.mutate({ day, patch: { dailyWorkingTime: Number(e.currentTarget.value) || null }, existing })}
                    />
                  </Table.Td>
                )}
                <Table.Td>
                  {existing && (
                    <ActionIcon size="sm" variant="subtle" color="red" aria-label="Rimuovi override" onClick={() => existing.id && remove.mutate(existing.id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
